import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import API_BASE_URL from '../api';
import { authFetch } from '../authFetch';
import useSelectedRadio from '../hooks/useSelectedRadio';
import './LiveWallSettingsPage.css';

const API = (API_BASE_URL || '').replace(/\/+$/, '');

const KPI_WIDGETS = [
  'activeListeners',
  'peakToday',
  'reach',
  'adr',
  'aha',
  'aqh',
  'tsl',
  'newListeners',
];

const MAIN_WIDGETS = ['audienceToday', 'geoMap', 'liveActivity', 'devices'];
const DETAIL_WIDGETS = ['activeListenersFeed'];
const WIDGET_LAYOUT_GROUPS = [
  {
    titleKey: 'topKpi',
    items: KPI_WIDGETS.map((key) => ({ group: 'kpi', key })),
  },
  {
    titleKey: 'centerRow',
    items: ['audienceToday', 'geoMap', 'liveActivity'].map((key) => ({ group: 'main', key })),
  },
  {
    titleKey: 'bottomRow',
    items: [
      { group: 'detail', key: 'activeListenersFeed' },
      { group: 'main', key: 'devices' },
    ],
  },
];

const defaultWidgets = {
  kpi: KPI_WIDGETS.reduce((acc, key) => ({ ...acc, [key]: true }), {}),
  main: MAIN_WIDGETS.reduce((acc, key) => ({ ...acc, [key]: true }), {}),
  detail: DETAIL_WIDGETS.reduce((acc, key) => ({ ...acc, [key]: true }), {}),
};

const defaultSettings = {
  enabled: false,
  token: '',
  publicUrl: '',
  stationLogoUrl: null,
  streamPlaybackUrl: '',
  playbackEnabled: true,
  template: 'balanced_live_wall_dark',
  widgets: defaultWidgets,
};

function mergeWidgets(widgets = {}) {
  return {
    kpi: { ...defaultWidgets.kpi, ...(widgets.kpi || {}) },
    main: { ...defaultWidgets.main, ...(widgets.main || {}) },
    detail: { ...defaultWidgets.detail, ...(widgets.detail || {}) },
  };
}

function normalizeSettings(payload = {}) {
  const settings = payload.settings || payload;
  const token =
    settings.publicToken ||
    settings.public_token ||
    settings.token ||
    settings.liveWallToken ||
    settings.live_wall_token ||
    '';

  return {
    enabled: !!(settings.enabled ?? settings.live_wall_enabled),
    token,
    publicUrl: settings.publicUrl || settings.public_url || settings.publicLink || settings.public_link || '',
    stationLogoUrl: settings.stationLogoUrl || settings.station_logo_url || null,
    streamPlaybackUrl: settings.streamPlaybackUrl || settings.stream_playback_url || '',
    playbackEnabled: settings.playbackEnabled ?? Boolean(settings.playback_enabled),
    template: settings.template || 'balanced_live_wall_dark',
    widgets: mergeWidgets(settings.widgets_config || settings.widgets || defaultWidgets),
  };
}

function buildPublicLink(settings, lang = 'uk') {
  if (settings.token) {
    const normalizedLang = ['uk', 'en', 'it', 'fr'].includes(String(lang).split('-')[0])
      ? String(lang).split('-')[0]
      : 'uk';
    return `${window.location.origin}/${normalizedLang}/live-wall/${encodeURIComponent(settings.token)}`;
  }
  return settings.publicUrl || '';
}

function getErrorMessage(error, fallback) {
  return error?.message || fallback;
}

function extractErrorMessage(data, fallback) {
  if (typeof data?.detail === 'string') return data.detail;
  if (Array.isArray(data?.detail)) {
    return data.detail.map((item) => item.msg || item.message || String(item)).join(', ');
  }
  return fallback;
}

export default function LiveWallSettingsPage() {
  const { t, i18n } = useTranslation('common');
  const { radioId: selectedRadioId, shortcode, name: selectedRadioName } = useSelectedRadio();

  const [radioId, setRadioId] = useState(selectedRadioId);
  const [stationName, setStationName] = useState(selectedRadioName || shortcode || '');
  const [settings, setSettings] = useState(defaultSettings);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [selectedLogoFile, setSelectedLogoFile] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState('');
  const [copyStatus, setCopyStatus] = useState('');
  const [tokenStatus, setTokenStatus] = useState('');
  const [saveStatus, setSaveStatus] = useState('');
  const [error, setError] = useState('');
  const [, setSuccess] = useState('');
  const [pressingButton, setPressingButton] = useState('');
  const pressTimerRef = useRef(null);

  const publicLink = useMemo(() => buildPublicLink(settings, i18n.language), [settings, i18n.language]);
  const pressClass = (key) => (pressingButton === key ? 'live-wall-settings-page__button-press' : '');

  const markButtonPress = useCallback((key) => {
    window.clearTimeout(pressTimerRef.current);
    setPressingButton(key);
    pressTimerRef.current = window.setTimeout(() => setPressingButton(''), 150);
  }, []);

  const resolveRadio = useCallback(async () => {
    if (selectedRadioId) {
      return { id: selectedRadioId, name: selectedRadioName || shortcode || '' };
    }

    if (!shortcode) {
      return { id: null, name: '' };
    }

    const radiosResp = await authFetch(`${API}/radios`);
    const radiosJson = await radiosResp.json();

    if (!radiosResp.ok || !Array.isArray(radiosJson)) {
      throw new Error(t('live_wall.settings.errors.load_stations'));
    }

    const currentRadio = radiosJson.find((item) => item.shortcode === shortcode);

    if (!currentRadio?.id) {
      throw new Error(t('live_wall.settings.errors.resolve_station'));
    }

    return { id: currentRadio.id, name: currentRadio.name || selectedRadioName || shortcode };
  }, [selectedRadioId, selectedRadioName, shortcode, t]);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    setTokenStatus('');
    setSaveStatus('');
    setCopyStatus('');
    setLogoError('');
    setSelectedLogoFile(null);

    try {
      const currentRadio = await resolveRadio();

      if (!currentRadio.id) {
        setRadioId(null);
        setStationName('');
        setSettings(defaultSettings);
        return;
      }

      setRadioId(currentRadio.id);
      setStationName(currentRadio.name);

      const resp = await authFetch(`${API}/radios/${currentRadio.id}/live-wall`);
      const json = await resp.json().catch(() => ({}));

      if (!resp.ok) {
        throw new Error(json?.detail || t('live_wall.settings.errors.load_settings'));
      }

      setSettings(normalizeSettings(json));
    } catch (e) {
      setRadioId(null);
      setSettings(defaultSettings);
      setError(getErrorMessage(e, t('live_wall.settings.errors.load_settings')));
    } finally {
      setLoading(false);
    }
  }, [resolveRadio, t]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => () => window.clearTimeout(pressTimerRef.current), []);

  const patchLocalSetting = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setSuccess('');
    setCopyStatus('');
    setSaveStatus('');
  };

  const patchWidget = (group, key, value) => {
    setSettings((prev) => ({
      ...prev,
      widgets: {
        ...prev.widgets,
        [group]: {
          ...prev.widgets[group],
          [key]: value,
        },
      },
    }));
    setSuccess('');
    setSaveStatus('');
  };

  const onToggleEnabled = async () => {
    if (!radioId) return;

    setToggling(true);
    setError('');
    setSuccess('');

    try {
      const endpoint = settings.enabled ? 'disable' : 'enable';
      const resp = await authFetch(`${API}/radios/${radioId}/live-wall/${endpoint}`, {
        method: 'POST',
      });
      const json = await resp.json().catch(() => ({}));

      if (!resp.ok) {
        throw new Error(json?.detail || t('live_wall.settings.errors.change_status'));
      }

      setSettings((prev) => ({
        ...prev,
        ...normalizeSettings({ ...prev, ...json }),
        enabled: endpoint === 'enable',
      }));
      setSuccess(endpoint === 'enable' ? t('live_wall.settings.status.enabled') : t('live_wall.settings.status.disabled'));
    } catch (e) {
      setError(getErrorMessage(e, t('live_wall.settings.errors.change_status')));
    } finally {
      setToggling(false);
    }
  };

  const onRegenerateToken = async () => {
    if (!radioId) return;

    setRegenerating(true);
    setError('');
    setSuccess('');
    setCopyStatus('');
    setTokenStatus('');
    setSaveStatus('');

    try {
      const resp = await authFetch(`${API}/radios/${radioId}/live-wall/regenerate-token`, {
        method: 'POST',
      });
      const json = await resp.json().catch(() => ({}));

      if (!resp.ok) {
        throw new Error(json?.detail || t('live_wall.settings.errors.update_token'));
      }

      setSettings((prev) => ({
        ...prev,
        ...normalizeSettings({ ...prev, ...json }),
      }));
      setTokenStatus(t('live_wall.settings.status.token_updated'));
    } catch (e) {
      setError(getErrorMessage(e, t('live_wall.settings.errors.update_token')));
    } finally {
      setRegenerating(false);
    }
  };

  const onCopy = async () => {
    if (!publicLink) return;

    try {
      await navigator.clipboard.writeText(publicLink);
      setCopyStatus(t('live_wall.settings.status.copied'));
      setTokenStatus('');
      setSaveStatus('');
    } catch {
      setCopyStatus(t('live_wall.settings.status.copy_failed'));
      setTokenStatus('');
      setSaveStatus('');
    }
  };

  const onLogoFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setLogoError('');
    setSuccess('');

    if (!file) {
      setSelectedLogoFile(null);
      return;
    }

    if (file.type !== 'image/png' && !file.name.toLowerCase().endsWith('.png')) {
      setSelectedLogoFile(null);
      setLogoError(t('live_wall.settings.errors.select_png'));
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setSelectedLogoFile(null);
      setLogoError(t('live_wall.settings.errors.png_size'));
      return;
    }

    setSelectedLogoFile(file);
  };

  const onUploadLogo = async () => {
    if (!radioId) {
      setLogoError(t('live_wall.settings.errors.select_station'));
      return;
    }

    if (!selectedLogoFile) {
      setLogoError(t('live_wall.settings.errors.select_png'));
      return;
    }

    setLogoUploading(true);
    setLogoError('');
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('logo', selectedLogoFile, selectedLogoFile.name);

      const resp = await authFetch(`${API}/radios/${radioId}/live-wall/logo`, {
        method: 'POST',
        body: formData,
      });
      const json = await resp.json().catch(() => ({}));

      if (!resp.ok) {
        throw new Error(extractErrorMessage(json, t('live_wall.settings.errors.upload_logo')));
      }

      setSettings((prev) => ({
        ...prev,
        ...normalizeSettings({ ...prev, ...json }),
      }));
      setSelectedLogoFile(null);
      setSuccess(t('live_wall.settings.status.logo_uploaded'));
    } catch (e) {
      setLogoError(getErrorMessage(e, t('live_wall.settings.errors.upload_logo')));
    } finally {
      setLogoUploading(false);
    }
  };

  const onSave = async () => {
    if (!radioId) return;

    setSaving(true);
    setError('');
    setSuccess('');
    setCopyStatus('');
    setTokenStatus('');
    setSaveStatus('');

    try {
      const resp = await authFetch(`${API}/radios/${radioId}/live-wall`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          stream_playback_url: settings.streamPlaybackUrl || null,
          playback_enabled: Boolean(settings.playbackEnabled),
          template: settings.template,
          widgets_config: settings.widgets,
        }),
      });
      const json = await resp.json().catch(() => ({}));

      if (!resp.ok) {
        throw new Error(json?.detail || t('live_wall.settings.errors.save_settings'));
      }

      setSettings((prev) => ({
        ...prev,
        ...normalizeSettings({ ...prev, ...json }),
      }));
      setSaveStatus(t('live_wall.settings.status.saved'));
    } catch (e) {
      setError(getErrorMessage(e, t('live_wall.settings.errors.save_settings')));
    } finally {
      setSaving(false);
    }
  };

  const disabled = loading || saving || toggling || regenerating || logoUploading;

  if (!shortcode && !selectedRadioId) {
    return (
      <section className="live-wall-settings-page">
        <h1>{t('nav.cabinet.live_wall')}</h1>
        <p className="live-wall-settings-page__description">
          {t('live_wall.settings.description')}
        </p>
        <div className="live-wall-settings-page__notice">{t('live_wall.settings.no_station')}</div>
      </section>
    );
  }

  return (
    <section className="live-wall-settings-page">
      <div className="live-wall-settings-page__header">
        <div>
          <h1>{t('nav.cabinet.live_wall')}</h1>
          <p className="live-wall-settings-page__description">
            {t('live_wall.settings.description')}
          </p>
        </div>
        <button
          type="button"
          className={`live-wall-settings-page__toggle ${settings.enabled ? 'is-enabled' : ''} ${pressClass('toggle')}`}
          onPointerDown={() => markButtonPress('toggle')}
          onClick={onToggleEnabled}
          disabled={!radioId || disabled}
        >
          {settings.enabled ? t('live_wall.settings.actions.disable') : t('live_wall.settings.actions.enable')}
        </button>
      </div>

      <div className="live-wall-settings-page__station">
        <span>{t('live_wall.settings.station')}</span>
        <strong>{stationName || shortcode}</strong>
      </div>

      {loading && <div className="live-wall-settings-page__notice">{t('common.loading')}</div>}
      {!!error && !loading && <div className="live-wall-settings-page__error">{error}</div>}

      {!loading && (
        <>
          <div className="live-wall-settings-page__grid">
            <div className="live-wall-settings-page__logo live-wall-settings-page__field--wide">
              <div>
                <h2>{t('live_wall.settings.logo.title')}</h2>
                <p>{t('live_wall.settings.logo.hint')}</p>
              </div>
              <div className="live-wall-settings-page__logo-body">
                <div className="live-wall-settings-page__logo-preview">
                  {settings.stationLogoUrl ? (
                    <img src={settings.stationLogoUrl} alt={t('live_wall.settings.logo.preview_alt')} />
                  ) : (
                    <span>RM</span>
                  )}
                </div>
                <div className="live-wall-settings-page__logo-controls">
                  <input type="file" accept=".png,image/png" onChange={onLogoFileChange} />
                  {selectedLogoFile && <small>{selectedLogoFile.name}</small>}
                  {logoError && <small className="live-wall-settings-page__logo-error">{logoError}</small>}
                  <button
                    type="button"
                    className={pressClass('upload-logo')}
                    onPointerDown={() => markButtonPress('upload-logo')}
                    onClick={onUploadLogo}
                    disabled={!radioId || disabled || !selectedLogoFile}
                  >
                    {logoUploading ? t('live_wall.settings.logo.uploading') : t('live_wall.settings.logo.upload')}
                  </button>
                </div>
              </div>
            </div>

            <label className="live-wall-settings-page__field live-wall-settings-page__field--wide">
              <span>{t('live_wall.settings.public_link')}</span>
              <div className="live-wall-settings-page__inline">
                <input type="text" value={publicLink} readOnly placeholder={t('live_wall.settings.public_link_placeholder')} />
                <button
                  type="button"
                  className={pressClass('copy')}
                  onPointerDown={() => markButtonPress('copy')}
                  onClick={onCopy}
                  disabled={!publicLink}
                >
                  {t('live_wall.settings.actions.copy')}
                </button>
              </div>
              {copyStatus && <small>{copyStatus}</small>}
            </label>

            <div className="live-wall-settings-page__field">
              <span>{t('live_wall.settings.public_token')}</span>
              <div className="live-wall-settings-page__inline">
                <input type="text" value={settings.token} readOnly placeholder={t('live_wall.settings.public_token_placeholder')} />
                <button
                  type="button"
                  className={pressClass('regenerate')}
                  onPointerDown={() => markButtonPress('regenerate')}
                  onClick={onRegenerateToken}
                  disabled={!radioId || disabled}
                >
                  {t('live_wall.settings.actions.regenerate_token')}
                </button>
              </div>
              {tokenStatus && <small>{tokenStatus}</small>}
            </div>

            <label className="live-wall-settings-page__field live-wall-settings-page__field--wide">
              <span>{t('live_wall.settings.stream_url')}</span>
              <input
                type="url"
                value={settings.streamPlaybackUrl}
                onChange={(e) => patchLocalSetting('streamPlaybackUrl', e.target.value)}
                placeholder="https://stream.example.com/live"
              />
            </label>

            <label className="live-wall-settings-page__checkbox live-wall-settings-page__field--wide">
              <input
                type="checkbox"
                checked={!!settings.playbackEnabled}
                onChange={(e) => patchLocalSetting('playbackEnabled', e.target.checked)}
              />
              <span>{t('live_wall.settings.allow_playback')}</span>
            </label>

            <label className="live-wall-settings-page__field">
              <span>{t('live_wall.settings.template')}</span>
              <select value={settings.template} onChange={(e) => patchLocalSetting('template', e.target.value)}>
                <option value="balanced_live_wall_dark">balanced_live_wall_dark</option>
              </select>
            </label>
          </div>

          <div className="live-wall-settings-page__widgets">
            <h2>{t('live_wall.settings.widgets.title')}</h2>
            <div className="live-wall-settings-page__widgets-groups">
              {WIDGET_LAYOUT_GROUPS.map((layoutGroup) => (
                <section className="live-wall-settings-page__widgets-group" key={layoutGroup.titleKey}>
                  <h3>{t(`live_wall.settings.widgets.groups.${layoutGroup.titleKey}`)}</h3>
                  <div className="live-wall-settings-page__widgets-grid">
                    {layoutGroup.items.map(({ group, key }) => (
                      <label key={`${group}-${key}`} className="live-wall-settings-page__checkbox">
                        <input
                          type="checkbox"
                          checked={!!settings.widgets[group][key]}
                          onChange={(e) => patchWidget(group, key, e.target.checked)}
                        />
                        <span>{t(`live_wall.settings.widgets.${key}`)}</span>
                      </label>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </div>

          <div className="live-wall-settings-page__actions">
            <button
              type="button"
              className={pressClass('save')}
              onPointerDown={() => markButtonPress('save')}
              onClick={onSave}
              disabled={!radioId || disabled}
            >
              {saving ? t('live_wall.settings.actions.saving') : t('live_wall.settings.actions.save')}
            </button>
            {saveStatus && <small>{saveStatus}</small>}
          </div>
        </>
      )}
    </section>
  );
}
